import { useState } from 'react';
import { coins, transactions } from '../../../utils/api';

export const useImportManager = ({ addLog, navigate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [createdCoins, setCreatedCoins] = useState([]);
  const [failedImports, setFailedImports] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const handleImport = async (rows, cleanRowData) => {
    setLoading(true);
    setError(null);
    setImportProgress({ current: 0, total: rows.length });
    setFailedImports([]); // Reset at start of import
    addLog('Starting import...');

    let successCount = 0;
    let errorCount = 0;
    const importedCoins = [];

    for (const rowData of rows) {
      try {
        // Skip empty or invalid rows
        if (!rowData || Object.keys(rowData).length === 0) {
          addLog('Skipping empty row', 'info');
          continue;
        }

        // Track original data for error reporting
        const originalDescription = rowData.description || 'Unknown Coin';
        const rowIndex = importProgress.current + 1;

        // Extract purchase details for transaction
        const purchasePrice = rowData.purchase_price ? parseFloat(rowData.purchase_price) : null;
        const purchaseDate = rowData.purchase_date || null;
        
        // Clean the data
        const coinData = cleanRowData(rowData);
        if (!coinData) continue;

        // Remove fields that shouldn't be sent to the API
        delete coinData.purchase_price;
        delete coinData.purchase_date;

        // Log cleaned coin data
        addLog(
          'Sending coin data to API',
          'info',
          'validation',
          `Cleaned data: ${JSON.stringify(coinData, null, 2)}`
        );

        // Only get next user coin ID if one wasn't provided
        if (!coinData.user_coin_id) {
          const { nextUserCoinId } = await coins.getNextUserCoinId();
          coinData.user_coin_id = nextUserCoinId;
        }
        coinData.status = coinData.status || 'Inventory';

        // Create coin
        const coin = await coins.create(coinData);
        addLog(
          'API response',
          'info',
          'validation',
          `Response: ${JSON.stringify(coin, null, 2)}`
        );
        importedCoins.push({
          ...coin,
          purchase_price: purchasePrice,
          purchase_date: purchaseDate
        });

        successCount++;
        setImportProgress(prev => ({ ...prev, current: prev.current + 1 }));
        addLog(`Successfully imported coin ${coin.id}`);
      } catch (err) {
        errorCount++;
        const errorMessage = err.response?.data?.error || err.message;
        const errorDetails = err.response?.data?.details || err.response?.data;
        
        // Track failed import with details
        setFailedImports(prev => [...prev, {
          description: originalDescription,
          rowNumber: rowIndex,
          error: typeof errorDetails === 'object' ? errorMessage : errorMessage,
          details: errorDetails
        }]);

        addLog(
          'Failed to import coin',
          'error',
          'validation',
          typeof errorDetails === 'object'
            ? `Error: ${errorMessage}\nDetails: ${JSON.stringify(errorDetails, null, 2)}`
            : `Error: ${errorMessage}`
        );
      }
    }

    setLoading(false);
    setImportProgress({ current: 0, total: 0 });

    if (successCount > 0) {
      // Only show transaction modal if any coins have purchase details
      const coinsWithPurchaseDetails = importedCoins.filter(coin => 
        coin.purchase_price || coin.purchase_date
      );
      
      // Create detailed completion message
      let completionMessage = `Import completed. ${successCount} coins imported successfully`;
      if (errorCount > 0) {
        completionMessage += `, ${errorCount} failed. Check the error summary below for details.`;
      } else {
        completionMessage += '.';
      }
      if (coinsWithPurchaseDetails.length > 0) {
        completionMessage += ` Creating purchase transaction for ${coinsWithPurchaseDetails.length} coin(s).`;
      }
      
      addLog(completionMessage);

      // If there were failures, add a summary
      if (failedImports.length > 0) {
        addLog(
          'Failed Imports Summary:',
          'error',
          'validation',
          failedImports.map(fail => 
            `Row ${fail.rowNumber}: ${fail.description} - ${fail.error}`
          ).join('\n')
        );
      }
      
      if (coinsWithPurchaseDetails.length > 0) {
        setCreatedCoins(coinsWithPurchaseDetails);
        setShowTransactionModal(true);
      } else {
        navigate('/inventory');
      }
    } else {
      setError('Import failed. Please check the logs for details.');
    }
  };

  const handleTransactionSubmit = async (transactionData, currentCoin) => {
    try {
      // Ensure transaction type is set to purchase and convert transaction_type to type
      const { transaction_type, document_ids, ...rest } = transactionData;
      
      if (currentCoin) {
        // Individual mode - create transaction for just this coin
        await transactions.create({
          ...rest,
          type: 'purchase',
          initialImport: true,
          document_ids: document_ids || [],
          coin_prices: {
            [currentCoin.id]: transactionData.coin_prices[currentCoin.id]
          }
        });
        addLog(`Created purchase transaction for coin ${currentCoin.user_coin_id}`);
      } else {
        // Batch mode - create one transaction for all coins
        await transactions.create({
          ...rest,
          type: 'purchase',
          initialImport: true,
          document_ids: document_ids || []
        });
        addLog('Created purchase transaction successfully');
        setShowTransactionModal(false);
        navigate('/inventory');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      const errorDetails = err.response?.data?.details || err.response?.data;
      
      addLog(
        'Failed to create purchase transaction',
        'error',
        'transaction',
        typeof errorDetails === 'object'
          ? `Error: ${errorMessage}\nDetails: ${JSON.stringify(errorDetails, null, 2)}`
          : `Error: ${errorMessage}`
      );
      setError('Failed to create purchase transaction. Check logs for details.');
    }
  };

  return {
    loading,
    error,
    showTransactionModal,
    createdCoins,
    failedImports,
    handleImport,
    handleTransactionSubmit,
    setShowTransactionModal,
    importProgress
  };
};
